import { Card, CardActions, CardContent } from '@mui/material'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { trim } from 'lodash'
import { FC } from 'react'
import CloudWiredIcon from '../../../../assets/icons/CloudWired.svg?react'
import CoinsIcon from '../../../../assets/icons/Coins.svg?react'
import Gift2DIcon from '../../../../assets/icons/Gift2D.svg?react'
import QuestionCircleIcon from '../../../../assets/icons/QuestionCircle.svg?react'
import ShieldCheck from '../../../../assets/icons/ShieldCheck.svg?react'
import StarsIcon from '../../../../assets/icons/Stars.svg?react'
import { useProfileStore } from '../../../../store/storeProfile'
import { TariffDTO, TariffType } from '../../../../swagger/dto'
import { TARIFF_MAP } from '../tariff.constants'
import { DiscountBadge } from './DiscountBadge'
import { PriceInfo } from './PriceInfo'

type Props = {
    tariff: TariffDTO
    currentPrice?: number
    isActive: boolean
    idTariffActive?: string
}

export const TariffCard: FC<Props> = ({ tariff, currentPrice, isActive, idTariffActive }) => {
    const {
        title,
        bonus_coins,
        price,
        params,
        description,
        id,
        tariff_type,
        discount_amount,
        price_with_discount,
    } = tariff
    const { color, icon } = TARIFF_MAP[tariff_type]
    const isInfinity = tariff_type === TariffType['INFINITY']
    const { deposit, isLoadingDeposit } = useProfileStore((state) => state)

    const hasDiscount = !!discount_amount

    const handleClick = () => {
        ym(100275765, 'reachGoal', `purchaseAA_${price}`)

        const paymentAmount = hasDiscount ? price_with_discount : price
        deposit({ amount: +trim(`${paymentAmount}`, ' '), tariff_uuid: id })
    }

    const textColor = isInfinity ? '#fff' : '#181B25'

    return (
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <Card
                sx={{
                    position: 'relative',
                    color: textColor,
                    background: isInfinity ? '#2E3038' : '#fff',
                    p: '8px 8px 24px 8px ',
                    borderRadius: '24px',
                    border: '1px solid #E9EAED',
                    mb: 2,
                }}>
                <CardContent
                    sx={{
                        p: 1,
                    }}>
                    {hasDiscount && <DiscountBadge discountPercent={discount_amount} />}
                    <Stack
                        sx={{
                            py: 2,
                            px: 1,
                            mb: 2,
                            borderRadius: '16px',
                            background: isInfinity ? '#40444F' : '#F9FAFB',
                        }}>
                        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
                            {icon}
                            <Typography gutterBottom variant="h5" component="div" color={textColor}>
                                {title}
                            </Typography>
                        </Stack>

                        <Chip
                            icon={<Gift2DIcon />}
                            label={`+ ${bonus_coins} коинов в подарок`}
                            sx={{
                                width: '230px',
                                background: color,
                                color: '#fff',
                                fontWeight: '500',
                                fontSize: '12px',
                                p: '4px 8px',
                                '& .MuiChip-icon': {
                                    m: '0px 4px 0px 0px',
                                },
                            }}
                        />
                    </Stack>

                    <PriceInfo
                        price={hasDiscount ? price_with_discount : price}
                        isInfinity={isInfinity}
                        discountedPrice={hasDiscount ? price : undefined}
                        color={textColor}
                    />

                    <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mt: 2, mb: 1 }}>
                        <CoinsIcon stroke={textColor} />
                        <Typography variant="body2" color={textColor}>
                            {price} коинов на {isInfinity ? 'год' : 'месяц'}
                        </Typography>
                    </Stack>

                    {params?.restrictions?.rag_size_mb && (
                        <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 1 }}>
                            <CloudWiredIcon stroke={textColor} />
                            <Typography variant="body2" color={textColor}>
                                {params.restrictions.rag_size_mb} (векторной базы RAG)
                                <Tooltip
                                    enterTouchDelay={0}
                                    placement="bottom-end"
                                    title="RAG позволяет вам хранить информацию в виде текста или файлов по той задаче, которую вы решаете. Она будет использоваться автоматически для значительного повышения качества и персонализации результата запроса.">
                                    <IconButton>
                                        <QuestionCircleIcon />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Stack>
                    )}

                    <Stack spacing={2} direction="row" sx={{ alignItems: 'center', mb: 2 }}>
                        <StarsIcon stroke={textColor} />
                        <Typography variant="body2" color={textColor}>
                            <div dangerouslySetInnerHTML={{ __html: description }} />
                        </Typography>
                    </Stack>
                </CardContent>

                <CardActions>
                    <Button
                        sx={{
                            backgroundColor:
                                idTariffActive === id && isActive ? `${color} !important` : '',
                        }}
                        disabled={
                            isLoadingDeposit ||
                            (!!currentPrice && price <= currentPrice && isActive)
                        }
                        size="small"
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={handleClick}>
                        {idTariffActive === id && isActive ? 'Тариф подключен' : 'Подключить'}
                    </Button>
                </CardActions>
            </Card>
        </Grid>
    )
}
